import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { submitContactForm, fetchGeolocation } from '../services/api';
import { toast } from 'sonner';

const countryCodes = [
  { code: '+1', country: 'US/CA', countryCode: 'US' },
  { code: '+44', country: 'UK', countryCode: 'GB' },
  { code: '+91', country: 'IN', countryCode: 'IN' },
  { code: '+61', country: 'AU', countryCode: 'AU' },
  { code: '+81', country: 'JP', countryCode: 'JP' },
  { code: '+31', country: 'NL', countryCode: 'NL' },
  { code: '+92', country: 'PK', countryCode: 'PK' },
  { code: '+49', country: 'DE', countryCode: 'DE' },
  { code: '+33', country: 'FR', countryCode: 'FR' },
  { code: '+971', country: 'AE', countryCode: 'AE' }
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [geoData, setGeoData] = useState(null);

  useEffect(() => {
    document.title = "Contact Us - IP Partners Inc";
    
    // Fetch geolocation on page load
    fetchGeolocation()
      .then(data => {
        if (data && data.isSuccess && data.data) {
          setGeoData(data);
          // Auto-set country based on geolocation
          const countryCode = data.data.countryCode;
          const matchedCountry = countryCodes.find(c => c.countryCode === countryCode);
          if (matchedCountry) {
            setFormData(prev => ({
              ...prev,
              countryCode: matchedCountry.code,
              country: matchedCountry.countryCode
            }));
          }
        }
      })
      .catch(error => {
        console.error('Failed to fetch geolocation:', error);
      });
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{7,15}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be between 7 and 15 digits';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setFormData({ ...formData, phone: value });
  };

  const handleCountryCodeChange = (e) => {
    const selectedCode = e.target.value;
    const matchedCountry = countryCodes.find(c => c.code === selectedCode);
    setFormData({ 
      ...formData, 
      countryCode: selectedCode,
      country: matchedCountry?.countryCode || 'US'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitContactForm(formData, geoData);
      
      toast.success(result.data?.message || 'Form submitted successfully!');
      
      // Form will redirect automatically on success
      // No need to reset as user will be redirected
    } catch (error) {
      toast.error(error.message || "Failed to submit form. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brown-100 to-brown-50 pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-brown-900 mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-brown-700 max-w-2xl mx-auto">
            Get in touch with our team for professional legal assistance
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-brown-900 mb-6">
                Let's Talk About Your Legal Needs
              </h2>
              <p className="text-lg text-brown-700 mb-8">
                Our team is ready to help you with your legal matters. Reach out to us through any of the methods below, and we'll get back to you promptly.
              </p>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-brown-100 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-brown-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-brown-900 mb-1">Office Address</h3>
                    <p className="text-brown-700">{companyInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-brown-100 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-brown-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-brown-900 mb-1">Phone</h3>
                    <a href={`tel:${companyInfo.phone}`} className="text-brown-700 hover:text-brown-800">
                      {companyInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-brown-100 p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-brown-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-brown-900 mb-1">Email</h3>
                    <a href={`mailto:${companyInfo.email}`} className="text-brown-700 hover:text-brown-800">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-brown-50 rounded-lg">
                <h3 className="font-semibold text-brown-900 mb-2">Office Hours</h3>
                <div className="space-y-1 text-brown-700">
                  <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                  <p>Saturday: 10:00 AM - 2:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-brown-50 rounded-xl p-8 shadow-lg border border-brown-200">
              <h2 className="text-2xl font-bold text-brown-900 mb-4">
                Send Us a Message
              </h2>
              <p className="text-brown-700 mb-6">
                Fill out the form below and one of our attorneys will contact you within 24 hours.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-brown-900 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none transition-colors ${
                      errors.name ? 'border-red-500' : 'border-brown-300'
                    }`}
                    placeholder="John Doe"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-brown-900 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none transition-colors ${
                      errors.email ? 'border-red-500' : 'border-brown-300'
                    }`}
                    placeholder="john@example.com"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-brown-900 mb-1">
                    Contact Number *
                  </label>
                  <div className="flex space-x-2">
                    <select
                      value={formData.countryCode}
                      onChange={handleCountryCodeChange}
                      className="px-3 py-2.5 border border-brown-300 rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none bg-white"
                    >
                      {countryCodes.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.code} ({item.country})
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      className={`flex-1 px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none transition-colors ${
                        errors.phone ? 'border-red-500' : 'border-brown-300'
                      }`}
                      placeholder="1234567890"
                    />
                  </div>
                  {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-brown-900 mb-1">
                    Describe Your Legal Needs *
                  </label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows="4"
                    className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none transition-colors resize-none ${
                      errors.message ? 'border-red-500' : 'border-brown-300'
                    }`}
                    placeholder="Tell us about your legal situation..."
                  />
                  {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brown-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-brown-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>

                <p className="text-xs text-brown-600 text-center">
                  By submitting this form, you agree to our Terms of Service and Privacy Policy.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
